import {
  Badge,
  Box,
  Button,
  Center,
  Flex,
  Heading,
  Link,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useMediaQuery,
} from '@chakra-ui/react';
import TableBox from '../../common/TableBox';
import { usePagination, useTable } from 'react-table';
import EmptyState from '../../../common/EmptyState';
import ReactTablePagination from '../../../common/ReactTablePagination';
import InvoiceStatus from './InvoiceStatus';
import { useEffect } from 'react';
import InvoiceMoreInfo from './InvoiceMoreInfo';

const BillingHistoryTable = ({
  columns,
  data,
  fetchData,
  isLoading,
  pageCount,
}) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setHiddenColumns,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      manualPagination: true,
      pageCount,
      initialState: { pageIndex: 0, pageSize: 10 },
    },
    usePagination
  );

  const [isLargerThan2XL] = useMediaQuery('(min-width: 96em)');
  const [isLargerThanXL] = useMediaQuery('(min-width: 80em)');
  const [isLargerThanLG] = useMediaQuery('(min-width: 62em)');
  const [isLargerThanMD] = useMediaQuery('(min-width: 48em)');
  const [isLargerThanSM] = useMediaQuery('(min-width: 30em)');
  const [isLargerThanS] = useMediaQuery('(min-width: 18em)');
  const [isLargerThanXS] = useMediaQuery('(min-width: 10em)');

  useEffect(() => {
    const updateHiddenColumns = () => {
      if (isLargerThan2XL) {
        setHiddenColumns(['moreInfo']);
      } else if (isLargerThanXL) {
        setHiddenColumns(['moreInfo']);
      } else if (isLargerThanLG) {
        setHiddenColumns(['plan', 'view']);
      } else if (isLargerThanMD) {
        setHiddenColumns(['plan', 'view']);
      } else if (isLargerThanSM) {
        setHiddenColumns(['plan', 'view', 'download']);
      } else if (isLargerThanS) {
        setHiddenColumns(['plan', 'status', 'view', 'download']);
      } else if (isLargerThanXS) {
        setHiddenColumns(['plan', 'status', 'view', 'download']);
      } else {
        setHiddenColumns(['plan', 'status', 'view', 'download']);
      }
    };

    updateHiddenColumns();
  }, [
    isLargerThan2XL,
    isLargerThanXL,
    isLargerThanLG,
    isLargerThanMD,
    isLargerThanSM,
    isLargerThanS,
    isLargerThanXS,
    setHiddenColumns,
  ]);

  useEffect(() => {
    fetchData({ pageIndex, pageSize });
  }, [fetchData, pageIndex, pageSize]);

  if (isLoading) {
    return (
      <Center h="100%" mt="50px">
        <Spinner />
      </Center>
    );
  }

  if (data.length === 0 && !isLoading) {
    return (
      <Flex
        direction="column"
        align="center"
        justify="center"
        h="100%"
        gap="10px"
        mt="50px"
        mb="30px"
      >
        <Text textStyle="heading4">No billing History</Text>
        <Text
          textStyle="bodyFamilyRegular"
          fontSize="16px"
          textAlign="center"
          w="100%"
        >
          No invoices found for this period
        </Text>
      </Flex>
    );
  }

  return (
    <TableBox>
      <Table {...getTableProps()}>
        <Thead>
          {headerGroups.map((headerGroup, index) => (
            <Tr {...headerGroup.getHeaderGroupProps()} key={index}>
              {headerGroup.headers.map((column, index) => (
                <Th
                  {...column.getHeaderProps()}
                  style={{
                    fontFamily: 'Gilroy Bold, sans-serif',
                    fontSize: '14px',
                  }}
                  key={index}
                >
                  {column.render('Header')}
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody {...getTableBodyProps()}>
          {page.map((row, index) => {
            prepareRow(row);
            return (
              <Tr
                {...row.getRowProps()}
                _hover={{
                  cursor: 'default',
                }}
                key={index}
              >
                {row.cells.map((cell, index) => {
                  return (
                    <Td
                      {...cell.getCellProps()}
                      style={cell.column.style}
                      key={index}
                    >
                      {cell.column.id === 'billingPeriod' ? (
                        <Text>
                          {new Date(
                            cell.row.original.attributes.billingPeriod
                          ).toLocaleDateString('en-US', {
                            month: 'long',
                            year: 'numeric',
                          })}
                        </Text>
                      ) : cell.column.id === 'plan' ? (
                        <Text>
                          {`${cell.row.original.attributes.plan}${cell.row.original.attributes.trialPeriodInvoice ? ' - Trial Period' : ''}`}
                        </Text>
                      ) : cell.column.id === 'status' ? (
                        <InvoiceStatus
                          status={
                            !cell.row.original.attributes.trialPeriodInvoice
                              ? cell.row.original.attributes.status
                              : 'trial'
                          }
                        />
                      ) : cell.column.id === 'view' ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          px="19px"
                          onClick={() => {
                            window.open(
                              cell.row.original.attributes.hostedInvoiceUrl,
                              '_blank'
                            );
                          }}
                        >
                          <Text>View</Text>
                        </Button>
                      ) : cell.column.id === 'download' ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          px="19px"
                          onClick={() => {
                            window.open(
                              cell.row.original.attributes.invoicePdf,
                              '_blank'
                            );
                          }}
                        >
                          <Text>Download</Text>
                        </Button>
                      ) : cell.column.id === 'errors' ? (
                        cell.row.original.attributes.messageType === 'Wallet' &&
                        cell.row.original.attributes.errorsCsv ? (
                          <Link to={cell.row.original.attributes.errorsCsv}>
                            <CustomCsvDownloadIcon color="brand" boxSize={7} />
                          </Link>
                        ) : cell.value > 0 ? (
                          <Badge
                            variant="outline"
                            colorScheme="red"
                            fontWeight="bold"
                            cursor="pointer"
                            onClick={() => {
                              cell.column.setTabIndex?.(1);
                            }}
                          >
                            {cell.value}
                          </Badge>
                        ) : null
                      ) : cell.column.id === 'moreInfo' ? (
                        <InvoiceMoreInfo cell={cell} />
                      ) : (
                        cell.render('Cell')
                      )}
                    </Td>
                  );
                })}
              </Tr>
            );
          })}
        </Tbody>
      </Table>
      {data.length > 0 && (
        <Flex
          mt={6}
          flexDirection={{ base: 'column-reverse', '2xl': 'row' }}
          justifyContent={{ base: 'none', '2xl': 'space-between' }}
        >
          <Stack
            spacing="18px"
            direction={{ base: 'column', sm: 'row' }}
            shouldWrapChildren="true"
          ></Stack>

          <ReactTablePagination
            canPreviousPage={canPreviousPage}
            canNextPage={canNextPage}
            pageOptions={pageOptions}
            pageCount={pageCount}
            gotoPage={gotoPage}
            nextPage={nextPage}
            previousPage={previousPage}
            setPageSize={setPageSize}
            pageIndex={pageIndex}
            pageSize={pageSize}
          />
        </Flex>
      )}
    </TableBox>
  );
};

export default BillingHistoryTable;
